import { useCallback, useMemo } from 'react';

// eslint-disable-next-line no-restricted-imports
import { isFedrampModerate } from '@atlaskit/atlassian-context/is-fedramp-moderate';
import { isIsolatedCloud } from '@atlaskit/atlassian-context/is-isolated-cloud';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import type {
	MediaEmojiProviderOptions,
	SyncBlockRendererProviderCreator,
} from '@atlaskit/editor-synced-block-provider/providers/types';
import EmojiResource from '@atlaskit/emoji/emoji-resource';
import { getConfluencePageAri } from '@atlaskit/editor-synced-block-provider/clients/confluence/ari';
import { useMemoizedBlockServiceAPIProviders } from '@atlaskit/editor-synced-block-provider/providers/block-service/blockServiceAPI';
import { useMemoizedSyncedBlockProvider } from '@atlaskit/editor-synced-block-provider/syncBlockProvider';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { AnalyticsEventPayload } from '@atlaskit/editor-common/analytics/types/events';
import {
	getSyncBlockNodesFromDoc,
	useMemoizedSyncedBlockNodeComponent,
} from '@atlaskit/editor-synced-block-renderer/use-synced-block-node-component';
import {
	useMemoizedSyncBlockStoreManager,
	type SyncBlockStoreManager,
} from '@atlaskit/editor-synced-block-provider/syncBlockStoreManager';
import type { SyncedBlockNodeProps } from '@atlaskit/editor-synced-block-renderer/synced-block-node-component-renderer';
import CardClient from '@atlaskit/link-provider/client';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory/conditional-hooks-factory';
import type { RendererContentMode } from '@atlaskit/renderer/types';

import { fg } from '@confluence/feature-gating';
import { ConfluenceEditorCardProvider } from '@confluence/smartlinks';
import { createCookie } from '@confluence/content-smartlinks/entry-points/createCookie.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { createRendererMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { getMediaSSRModeAndAuthConfigMemoized } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers.ts';
import {
	useRouteActions,
	useMatchRoute,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

import { getEmojiProvider } from '../entry-points/EmojiProvider.ts';

import { createRelaySyncBlockFetchProvider } from './relaySyncBlockFetchProvider.ts';
import { createSmartCardEventHandler } from './utils/createSmartCardEventHandler.ts';
import { syncedBlockLoadSSRData } from './SyncedBlockLoadSSRData.ts';

export const useMemoizedSyncedBlockNestedRendererProviderCreator = (
	cloudId: string,
	userId: string | undefined | null,
	spaceKey?: string,
): SyncBlockRendererProviderCreator => {
	return useMemo(
		() => ({
			createEmojiProvider: () => {
				return getEmojiProvider(EmojiResource, {
					cloudId,
					userId: userId || undefined,
					allowAnonymousAccess: true,
				});
			},
			createMediaProvider: ({
				contentAri,
				contentId: refSyncBlockContentId,
				contentProduct,
			}: MediaEmojiProviderOptions) => {
				return createRendererMediaProvider({
					contentAri,
					contentId: refSyncBlockContentId,
					contentProduct,
					isExternalShare: false,
				});
			},
			createSSRMediaProvider: ({
				contentAri,
				contentId: refSyncBlockContentId,
				contentProduct,
			}: MediaEmojiProviderOptions) => {
				const mediaClientConfig = getMediaSSRModeAndAuthConfigMemoized({
					contentAri,
					contentId: refSyncBlockContentId,
					contentProduct,
					isExternalShare: false,
					spaceKey,
				});

				if (mediaClientConfig) {
					// Return config for both server (SSR) and client (post-hydration) so synced block
					// media uses the source contentId for auth, not the current page's contentId.
					return {
						viewMediaClientConfig: mediaClientConfig.config,
					};
				}

				return undefined;
			},
			// Uses the same logic as in preloadEditorNodeDataProvidersData.ts
			createSmartLinkProvider: () => {
				const { host, cloudId: ssrCloudId, userContext, customURLs } = getSSRRenderInputs();
				const objectResolverURL =
					customURLs?.['object-resolver-service']?.match(/^(https?:\/\/[^/]+)/)?.[0];

				if (objectResolverURL) {
					const ssrCardClient = new CardClient('custom', objectResolverURL);
					ssrCardClient.setHeaders({
						Cookie: createCookie(userContext),
						Origin: host.startsWith('http') ? host : `https://${host}`,
						CloudId: ssrCloudId || cloudId,
					});

					return Promise.resolve(
						new ConfluenceEditorCardProvider('custom', objectResolverURL, undefined, ssrCardClient),
					);
				}

				return Promise.resolve(new ConfluenceEditorCardProvider());
			},
		}),
		[cloudId, userId, spaceKey],
	);
};

type ProviderDefinitions = {
	[name: string]: Promise<any>;
};

/** Same shape as page sticky header config so tables in synced blocks use the same scroll root (e.g. AkMainContent). */
export type SyncedBlockStickyHeadersConfig = {
	offsetTop: number;
	defaultScrollRootId_DO_NOT_USE?: string;
	shouldAddDefaultScrollRootOffsetTop_DO_NOT_USE?: boolean;
};

export type SyncedBlockProviderNodeComponentProps = {
	doc: DocNode;
	contentId: string | undefined;
	spaceKey?: string;
	contentType: string | undefined;
	mergedDataProviderDefinitions: ProviderDefinitions;
	/** When set, nested renderer uses this scroll root and offset so sticky table headers work on the page. */
	stickyHeadersConfig?: SyncedBlockStickyHeadersConfig;
	/** The content mode (e.g. compact) of the parent page, which will also be applied to the reference blocks on the page. */
	contentMode?: RendererContentMode;
};

export type SyncBlockStoreManagerForContentProps = {
	contentId: string | undefined;
	contentType: string | undefined;
};

/**
 * Builds a `SyncBlockStoreManager` scoped to the given Confluence content, using
 * the same Block Service provider chain as the reference-block renderer. Because
 * `createSyncBlockStoreManager` shares a module-level in-memory session cache,
 * this manager reuses data already fetched by the renderer (and vice versa) — so
 * consumers such as the Table of Contents can read synced-block content and
 * subscribe to live updates without firing duplicate requests.
 *
 * Returns `undefined` when there is no content id (nothing to scope to).
 */
export const useSyncBlockStoreManagerForContent = ({
	contentId: confluencePageId,
	contentType,
}: SyncBlockStoreManagerForContentProps): SyncBlockStoreManager | undefined => {
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const confluencePageAri = useMemo(
		() =>
			confluencePageId
				? getConfluencePageAri({
						pageId: confluencePageId,
						cloudId,
						pageType: contentType === 'page' ? 'page' : 'blogpost',
					})
				: undefined,
		[cloudId, confluencePageId, contentType],
	);

	const { fetchProvider: blockServiceFetchProvider, writeProvider: blockServiceWriteProvider } =
		useMemoizedBlockServiceAPIProviders({
			parentAri: confluencePageAri ?? '',
			parentId: confluencePageId ?? '',
			product: 'confluence-page',
			cloudId,
		});

	const relayFetchProvider = useMemo(
		() => createRelaySyncBlockFetchProvider(blockServiceFetchProvider, cloudId),
		[blockServiceFetchProvider, cloudId],
	);

	// The TOC only reads content, but the synced-block provider still needs its
	// write provider to derive reference resource ids when same-page sync is
	// enabled. Supplying it here does not perform any writes.
	const readOnlyProviderOptions = useMemo(() => ({}), []);
	const syncedBlockProvider = useMemoizedSyncedBlockProvider({
		fetchProvider: relayFetchProvider,
		writeProvider: blockServiceWriteProvider,
		providerOptions: readOnlyProviderOptions,
		getSSRData: syncedBlockLoadSSRData,
	});

	const handleSyncedBlockAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: payload,
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const storeManager = useMemoizedSyncBlockStoreManager(
		syncedBlockProvider,
		handleSyncedBlockAnalyticsEvent,
	);

	return confluencePageId ? storeManager : undefined;
};

const useMemoizedSyncedBlockProviderNodeComponentEnabled = ({
	doc,
	spaceKey,
	contentId: confluencePageId,
	contentType,
	mergedDataProviderDefinitions,
	stickyHeadersConfig,
	contentMode,
}: SyncedBlockProviderNodeComponentProps):
	| ((props: SyncedBlockNodeProps) => React.JSX.Element)
	| undefined => {
	const syncBlockNodes = useMemo(() => {
		return getSyncBlockNodesFromDoc(doc);
	}, [doc]);
	const { cloudId, userId } = useSessionData();

	const confluencePageAri = useMemo(
		() =>
			confluencePageId
				? getConfluencePageAri({
						pageId: confluencePageId,
						cloudId,
						pageType: contentType === 'page' ? 'page' : 'blogpost',
					})
				: undefined,
		[cloudId, confluencePageId, contentType],
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fetchProvider: blockServiceFetchProvider, writeProvider: blockServiceWriteProvider } =
		useMemoizedBlockServiceAPIProviders({
			parentAri: confluencePageAri ?? '',
			parentId: confluencePageId ?? '',
			product: 'confluence-page',
			cloudId,
		});

	// Wrap the fetch provider with Relay subscription support
	const relayFetchProvider = useMemo(
		() => createRelaySyncBlockFetchProvider(blockServiceFetchProvider, cloudId),
		[blockServiceFetchProvider, cloudId],
	);

	const syncedBlockNestedRendererProviderCreator =
		useMemoizedSyncedBlockNestedRendererProviderCreator(cloudId, userId, spaceKey);
	const providerOptions = useMemo(() => {
		return {
			parentDataProviders: {
				...mergedDataProviderDefinitions,
			},
			providerCreator: syncedBlockNestedRendererProviderCreator,
		};
	}, [mergedDataProviderDefinitions, syncedBlockNestedRendererProviderCreator]);
	const syncedBlockProvider = useMemoizedSyncedBlockProvider({
		fetchProvider: relayFetchProvider,
		writeProvider: blockServiceWriteProvider,
		providerOptions,
		getSSRData: syncedBlockLoadSSRData,
	});

	const handleSyncedBlockAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: payload,
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const { push } = useRouteActions();
	const matchRoute = useMatchRoute();
	const syncedBlockEventHandlers = useMemo(() => {
		return createSmartCardEventHandler({ matchRoute, push });
	}, [matchRoute, push]);

	// When the synced-block ToC experiment is on, enable heading anchor links
	// inside synced blocks. We intentionally do NOT provide a `getHeadingLink`
	// override: omitting it makes the renderer fall back to the platform default
	// (current view page URL + hash), which matches the copy-link behaviour of
	// every other heading on the page.
	const allowHeadingAnchorLinks = useMemo(
		() =>
			expValEquals('confluence_toc_synced_block_exp', 'isEnabled', true)
				? { allowNestedHeaderLinks: true }
				: undefined,
		[],
	);

	// An inline object gives the memoised node component a new identity each render,
	// remounting the subtree and destroying any in-progress text selection.
	const memoizedSyncBlockRendererOptions = useMemo(
		() => ({
			smartLinks: {
				ssr: true,
			},
			eventHandlers: syncedBlockEventHandlers,
			stickyHeaders: stickyHeadersConfig
				? { show: true, ...stickyHeadersConfig }
				: { show: true, offsetTop: 0 },
			contentMode,
			allowHeadingAnchorLinks,
		}),
		[syncedBlockEventHandlers, stickyHeadersConfig, contentMode, allowHeadingAnchorLinks],
	);

	const SyncedBlockNodeComponent = useMemoizedSyncedBlockNodeComponent({
		syncBlockProvider: syncedBlockProvider,
		syncBlockNodes,
		syncBlockRendererOptions: fg('platform_editor_blocks_patch_7')
			? memoizedSyncBlockRendererOptions
			: {
					smartLinks: {
						ssr: true,
					},
					eventHandlers: syncedBlockEventHandlers,
					stickyHeaders: stickyHeadersConfig
						? { show: true, ...stickyHeadersConfig }
						: { show: true, offsetTop: 0 },
					contentMode,
					// Must be forwarded on this branch too. `allowHeadingAnchorLinks`
					// is gated by its own `confluence_toc_synced_block_exp` experiment,
					// so it must not be coupled to `platform_editor_blocks_patch_7`
					// (an unrelated editor patch gate that only controls whether the
					// options object is memoised). Omitting it here leaves the renderer
					// with no anchor-link config whenever that patch gate is off, which
					// disables heading ids inside synced blocks — breaking ToC deep
					// links — and hides the heading copy-link button.
					allowHeadingAnchorLinks,
				},
		fireAnalyticsEvent: handleSyncedBlockAnalyticsEvent,
	});

	// If confluencePageId is undefined, don't return the synced block node component, and don't render synced blocks
	return confluencePageId ? SyncedBlockNodeComponent : undefined;
};

export const useMemoizedSyncedBlockProviderNodeComponentDisabled = (
	_props: SyncedBlockProviderNodeComponentProps,
): ((props: SyncedBlockNodeProps) => React.JSX.Element) | undefined => {
	return undefined;
};

const isFedrampOrIsolatedCloud = (): boolean => {
	return isFedrampModerate() || isIsolatedCloud();
};

export const useMemoizedSyncedBlockProviderNodeComponent: (
	props: SyncedBlockProviderNodeComponentProps,
) => ((props: SyncedBlockNodeProps) => React.JSX.Element) | undefined = conditionalHooksFactory(
	() => !isFedrampOrIsolatedCloud(),
	useMemoizedSyncedBlockProviderNodeComponentEnabled,
	useMemoizedSyncedBlockProviderNodeComponentDisabled,
);
